/**
 * ProductEntryForm Component
 * 
 * Main form for entering product data with field retention support.
 * Provides three action buttons: Create, Create & Add New, Create New.
 * 
 * Requirements: 1.1, 1.2, 1.3, 1.4, 8.1, 8.2, 8.3, 8.4, 8.5
 */

import { useState, useCallback } from 'react';
import { useProductForm } from '../hooks/useProductForm';
import { SKUGenerator } from './SKUGenerator';
import { InternalSKUField, type ValidationStatus } from './InternalSKUField';
import { SupplierURLField } from './SupplierURLField';
import { AttributeList } from './AttributeList';
import { YMMEntryList } from './YMMEntryList';
import { CategorySelector } from './CategorySelector';
import { BrandSelector } from './BrandSelector';
import { ImageCapture } from './ImageCapture';
import { createProduct, validateProductForm } from '../api/products';
import type { ValidationError } from '../api/products';
import type { RetainableField, SubmitAction, ProductAttribute, YMMRestriction, ImageData } from '../types/product';

interface FieldWithRetentionProps {
  label: string;
  fieldName: RetainableField;
  isRetained: boolean;
  onToggleRetention: (field: RetainableField) => void;
  children: React.ReactNode;
}

/**
 * Wrapper component that adds retention checkbox to a form field
 */
function FieldWithRetention({
  label,
  fieldName,
  isRetained,
  onToggleRetention,
  children,
}: FieldWithRetentionProps) {
  return (
    <div className="form-field-with-retention">
      <div className="field-header">
        <label htmlFor={fieldName}>{label}</label>
        <label className="retention-checkbox">
          <input
            type="checkbox"
            checked={isRetained}
            onChange={() => onToggleRetention(fieldName)}
            aria-label={`Retain ${label} for next entry`}
          />
          <span className="retention-label">Keep</span>
        </label>
      </div>
      {children}
    </div>
  );
}

export interface ProductEntryFormProps {
  onSubmit?: (action: SubmitAction) => Promise<void>;
  onProductCreated?: (productId: number, productName: string) => void;
}

export function ProductEntryForm({ onSubmit, onProductCreated }: ProductEntryFormProps) {
  const {
    formData,
    updateField,
    toggleRetention,
    isRetained,
    resetForm,
  } = useProductForm();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  // Track internal SKU validation status for form submission validation
  const [internalSkuStatus, setInternalSkuStatus] = useState<ValidationStatus>('idle');

  /**
   * Check if a field has a validation error
   */
  const hasFieldError = useCallback((fieldName: string): boolean => {
    return validationErrors.some(e => e.field === fieldName || e.field.startsWith(`${fieldName}[`));
  }, [validationErrors]);

  /**
   * Get validation error message for a field
   */
  const getFieldError = useCallback((fieldName: string): string | undefined => {
    const error = validationErrors.find(e => e.field === fieldName);
    return error?.message;
  }, [validationErrors]);

  /**
   * Handle form submission with validation, image upload, and product creation
   * Requirements: 1.2, 8.2, 8.3, 8.4
   */
  const handleSubmit = async (action: SubmitAction) => {
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);
    setValidationErrors([]);
    setUploadProgress(null);

    try {
      // Client-side validation
      const errors = validateProductForm(formData);
      
      // Check internal SKU status
      if (internalSkuStatus === 'duplicate') {
        errors.push({ field: 'internalSku', message: 'Internal SKU already exists' });
      }
      if (internalSkuStatus === 'checking') {
        errors.push({ field: 'internalSku', message: 'Please wait for SKU validation to complete' });
      }

      if (errors.length > 0) {
        setValidationErrors(errors);
        setSubmitError(`Please fix ${errors.length} validation error${errors.length > 1 ? 's' : ''}`);
        return;
      }

      // Call custom onSubmit if provided (for testing/custom handling)
      if (onSubmit) {
        await onSubmit(action);
      }

      // Create product (uploads images first, then creates product)
      const result = await createProduct(formData, (progress) => {
        setUploadProgress(progress);
      });

      // Success!
      setSubmitSuccess(`Product "${result.name}" created successfully! (ID: ${result.id})`);
      
      // Notify parent component
      if (onProductCreated) {
        onProductCreated(result.id, result.name);
      }

      // Reset form based on action
      resetForm(action);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create product';
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
      setUploadProgress(null);
    }
  };

  return (
    <form className="product-entry-form" onSubmit={(e) => e.preventDefault()}>
      {/* Success message */}
      {submitSuccess && (
        <div className="form-success" role="status" aria-live="polite">
          <span className="success-icon">✓</span>
          {submitSuccess}
        </div>
      )}

      {/* Error message */}
      {submitError && (
        <div className="form-error" role="alert" aria-live="assertive">
          <span className="error-icon">⚠</span>
          {submitError}
        </div>
      )}

      {/* Upload progress indicator */}
      {uploadProgress !== null && (
        <div className="upload-progress" role="status" aria-live="polite">
          <span>Uploading images...</span>
          <progress value={uploadProgress} max={100}>{uploadProgress}%</progress>
          <span>{uploadProgress}%</span>
        </div>
      )}

      {/* SKU Management Section */}
      <section className="form-section">
        <h2>SKU Management</h2>
        
        <FieldWithRetention
          label="Internal SKU"
          fieldName="internalSku"
          isRetained={isRetained('internalSku')}
          onToggleRetention={toggleRetention}
        >
          <InternalSKUField
            value={formData.internalSku}
            onChange={(value) => updateField('internalSku', value)}
            onValidationChange={(status) => setInternalSkuStatus(status)}
          />
        </FieldWithRetention>

        <FieldWithRetention
          label="WooCommerce SKU"
          fieldName="woocommerceSku"
          isRetained={isRetained('woocommerceSku')}
          onToggleRetention={toggleRetention}
        >
          <SKUGenerator
            value={formData.woocommerceSku}
            onChange={(value) => updateField('woocommerceSku', value)}
            prefix={formData.skuPrefix}
            onPrefixChange={(value) => updateField('skuPrefix', value)}
            brand={formData.skuBrand}
            onBrandChange={(value) => updateField('skuBrand', value)}
          />
        </FieldWithRetention>

        <FieldWithRetention
          label="Supplier Reorder URL"
          fieldName="supplierReorderUrl"
          isRetained={isRetained('supplierReorderUrl')}
          onToggleRetention={toggleRetention}
        >
          <SupplierURLField
            value={formData.supplierReorderUrl}
            onChange={(value) => updateField('supplierReorderUrl', value)}
          />
        </FieldWithRetention>
      </section>

      {/* Basic Info Section */}
      <section className="form-section">
        <h2>Basic Information</h2>
        
        <FieldWithRetention
          label="Product Name"
          fieldName="name"
          isRetained={isRetained('name')}
          onToggleRetention={toggleRetention}
        >
          <input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => updateField('name', e.target.value)}
            placeholder="Enter product name"
            required
            className={hasFieldError('name') ? 'input-error' : ''}
            aria-invalid={hasFieldError('name')}
          />
          {getFieldError('name') && (
            <span className="field-error">{getFieldError('name')}</span>
          )}
        </FieldWithRetention>

        <FieldWithRetention
          label="Description"
          fieldName="description"
          isRetained={isRetained('description')}
          onToggleRetention={toggleRetention}
        >
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => updateField('description', e.target.value)}
            placeholder="Enter product description"
            rows={4}
          />
        </FieldWithRetention>

        <FieldWithRetention
          label="Short Description"
          fieldName="shortDescription"
          isRetained={isRetained('shortDescription')}
          onToggleRetention={toggleRetention}
        >
          <textarea
            id="shortDescription"
            value={formData.shortDescription}
            onChange={(e) => updateField('shortDescription', e.target.value)}
            placeholder="Enter short description"
            rows={2}
          />
        </FieldWithRetention>
      </section>

      {/* Pricing Section */}
      <section className="form-section">
        <h2>Pricing</h2>
        
        <FieldWithRetention
          label="Regular Price"
          fieldName="regularPrice"
          isRetained={isRetained('regularPrice')}
          onToggleRetention={toggleRetention}
        >
          <input
            type="text"
            id="regularPrice"
            value={formData.regularPrice}
            onChange={(e) => updateField('regularPrice', e.target.value)}
            placeholder="0.00"
            inputMode="decimal"
            className={hasFieldError('regularPrice') ? 'input-error' : ''}
            aria-invalid={hasFieldError('regularPrice')}
          />
          {getFieldError('regularPrice') && (
            <span className="field-error">{getFieldError('regularPrice')}</span>
          )}
        </FieldWithRetention>
      </section>

      {/* Inventory Section */}
      <section className="form-section">
        <h2>Inventory</h2>
        
        <FieldWithRetention
          label="Stock Quantity"
          fieldName="stockQuantity"
          isRetained={isRetained('stockQuantity')}
          onToggleRetention={toggleRetention}
        >
          <input
            type="number"
            id="stockQuantity"
            value={formData.stockQuantity}
            onChange={(e) => updateField('stockQuantity', parseInt(e.target.value) || 0)}
            min="0"
            inputMode="numeric"
          />
        </FieldWithRetention>
      </section>

      {/* YMM Section */}
      <section className="form-section">
        <YMMEntryList
          restrictions={formData.ymmRestrictions}
          onChange={(restrictions: YMMRestriction[]) => updateField('ymmRestrictions', restrictions)}
        />
        {validationErrors.some(e => e.field.startsWith('ymmRestrictions')) && (
          <div className="section-errors">
            {validationErrors
              .filter(e => e.field.startsWith('ymmRestrictions'))
              .map((e, i) => (
                <span key={i} className="field-error">{e.message}</span>
              ))}
          </div>
        )}
      </section>

      {/* Categories & Brand Section */}
      <section className="form-section">
        <h2>Categories & Brand</h2>
        
        <FieldWithRetention
          label="Categories"
          fieldName="categoryIds"
          isRetained={isRetained('categoryIds')}
          onToggleRetention={toggleRetention}
        >
          <CategorySelector
            value={formData.categoryIds}
            onChange={(categoryIds: number[]) => updateField('categoryIds', categoryIds)}
            disabled={isSubmitting}
          />
        </FieldWithRetention>

        <FieldWithRetention
          label="Brand"
          fieldName="brandId"
          isRetained={isRetained('brandId')}
          onToggleRetention={toggleRetention}
        >
          <BrandSelector
            value={formData.brandId}
            onChange={(brandId: number | null) => updateField('brandId', brandId)}
            disabled={isSubmitting}
          />
        </FieldWithRetention>
      </section>

      {/* Attributes Section */}
      <section className="form-section">
        <AttributeList
          attributes={formData.attributes}
          onChange={(attributes: ProductAttribute[]) => updateField('attributes', attributes)}
        />
      </section>

      {/* Images Section */}
      <section className="form-section">
        <h2>Images</h2>
        <ImageCapture
          images={formData.images}
          onChange={(images: ImageData[]) => updateField('images', images)}
          maxImages={10}
        />
      </section>

      {/* Action Buttons */}
      <div className="form-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => handleSubmit('create')}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Creating...' : 'Create'}
        </button>
        
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => handleSubmit('createAndAddNew')}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Creating...' : 'Create & Add New'}
        </button>
        
        <button
          type="button"
          className="btn btn-tertiary"
          onClick={() => handleSubmit('createNew')}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Creating...' : 'Create New'}
        </button>
      </div>
    </form>
  );
}

export default ProductEntryForm;
